/**
 * Data Migration Module
 *
 * Handles one-time migration of data from IndexedDB to Supabase
 * when a user links their account.
 */

import { supabase } from '../supabase';
import { getOfflineDataStore } from './offline';
import type { Song, Performance } from './types';

const MIGRATION_KEY = 'singlist-data-migrated';

/**
 * Check if migration has already been performed for this user.
 */
export function hasMigrated(userId: string): boolean {
  const migrated = localStorage.getItem(MIGRATION_KEY);
  if (!migrated) return false;

  try {
    const migratedUsers = JSON.parse(migrated) as string[];
    return migratedUsers.includes(userId);
  } catch {
    return false;
  }
}

/**
 * Mark migration as complete for a user.
 */
function markMigrated(userId: string): void {
  const migrated = localStorage.getItem(MIGRATION_KEY);
  let migratedUsers: string[] = [];

  try {
    if (migrated) {
      migratedUsers = JSON.parse(migrated) as string[];
    }
  } catch {
    migratedUsers = [];
  }

  if (!migratedUsers.includes(userId)) {
    migratedUsers.push(userId);
    localStorage.setItem(MIGRATION_KEY, JSON.stringify(migratedUsers));
  }
}

/**
 * Migrate all data from IndexedDB to Supabase for a user.
 * This is a one-time operation when linking an account.
 *
 * @param offlineUserId - The offline user ID (from IndexedDB)
 * @param supabaseUserId - The new Supabase user ID
 * @returns Migration result with counts
 */
export async function migrateToSupabase(
  offlineUserId: string,
  supabaseUserId: string
): Promise<{
  success: boolean;
  songsMigrated: number;
  performancesMigrated: number;
  errors: string[];
}> {
  const errors: string[] = [];
  let songsMigrated = 0;
  let performancesMigrated = 0;

  // Check if already migrated
  if (hasMigrated(supabaseUserId)) {
    return { success: true, songsMigrated: 0, performancesMigrated: 0, errors: [] };
  }

  try {
    const offlineStore = getOfflineDataStore();
    await offlineStore.initialize();

    // Get all songs from IndexedDB
    const songsResult = await offlineStore.songs.getAll(offlineUserId, { includeDeleted: true });

    if (songsResult.error) {
      errors.push(`Failed to read offline songs: ${songsResult.error.message}`);
      return { success: false, songsMigrated, performancesMigrated, errors };
    }

    const offlineSongs = songsResult.data || [];

    // Map old song IDs to new IDs for performance migration
    const songIdMap = new Map<string, string>();

    // Migrate songs
    for (const song of offlineSongs) {
      try {
        // Create song data for Supabase (with new user_id, keeping other fields)
        const songData: Partial<Song> = {
          user_id: supabaseUserId,
          title: song.title,
          artist: song.artist,
          album: song.album,
          album_art_url: song.album_art_url,
          duration_seconds: song.duration_seconds,
          difficulty: song.difficulty,
          vocal_range_low: song.vocal_range_low,
          vocal_range_high: song.vocal_range_high,
          vocal_range_label: song.vocal_range_label,
          genre: song.genre,
          release_year: song.release_year,
          is_duet: song.is_duet,
          notes: song.notes,
          music_service: song.music_service,
          music_service_track_id: song.music_service_track_id,
          deleted_at: song.deleted_at,
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase.from('songs') as any)
          .insert(songData)
          .select('id')
          .single();

        if (error) {
          // Skip duplicates (e.g., same music_service_track_id)
          if (error.code === '23505') {
            continue;
          }
          errors.push(`Failed to migrate song "${song.title}": ${error.message}`);
          continue;
        }

        songIdMap.set(song.id, data.id);
        songsMigrated++;
      } catch (err) {
        errors.push(`Error migrating song "${song.title}": ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }

    // Migrate performances for migrated songs
    for (const [oldSongId, newSongId] of songIdMap) {
      try {
        const perfsResult = await offlineStore.performances.getAll(oldSongId);

        if (perfsResult.error) {
          errors.push(`Failed to read performances for song ${oldSongId}: ${perfsResult.error.message}`);
          continue;
        }

        const performances = perfsResult.data || [];

        for (const perf of performances) {
          const perfData: Partial<Performance> = {
            song_id: newSongId,
            user_id: supabaseUserId,
            performed_at: perf.performed_at,
            rating: perf.rating,
            notes: perf.notes,
          };

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { error } = await (supabase.from('performances') as any)
            .insert(perfData);

          if (error) {
            errors.push(`Failed to migrate performance: ${error.message}`);
            continue;
          }

          performancesMigrated++;
        }
      } catch (err) {
        errors.push(`Error migrating performances: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }

    // Mark migration as complete
    markMigrated(supabaseUserId);

    return {
      success: errors.length === 0,
      songsMigrated,
      performancesMigrated,
      errors,
    };
  } catch (err) {
    errors.push(`Migration failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    return { success: false, songsMigrated, performancesMigrated, errors };
  }
}
